import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { api } from "@/lib/api";
import { OrderView } from "@/pages/OrderPage";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { t } = useLang();
  const { clear } = useCart();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    let tries = 0;
    let timer;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (!alive) return;
        if (data?.payment_status === "paid") {
          setState("paid");
          clear();
          if (data.order_token) {
            const o = await api.get(`/orders/${data.order_token}`);
            if (alive) setOrder(o.data);
          }
          return;
        }
        if (["expired", "failed"].includes(data?.payment_status)) return setState("failed");
      } catch (e) {
        if (!alive) return;
      }
      tries += 1;
      if (tries >= 10) setState("failed");
      else timer = setTimeout(poll, 2000);
    };
    if (sid) poll();
    else setState("failed");
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [sid, clear]);

  return (
    <main className="grain relative min-h-screen bg-[#0B0B0C] px-5 pb-24 pt-36 text-[#F7F3EB]" data-testid="payment-success-page">
      {state === "checking" && (
        <div className="flex flex-col items-center gap-4 text-center" data-testid="payment-verifying">
          <Loader2 className="h-6 w-6 animate-spin text-[#C6A76A]" />
          <p className="font-display text-2xl">{t("verifying_payment")}</p>
        </div>
      )}
      {state === "paid" && order && (
        <OrderView order={order}>
          <p className="mt-4 text-sm text-[#C6A76A]" data-testid="payment-confirmed">{t("payment_ok")}</p>
        </OrderView>
      )}
      {state === "failed" && (
        <div className="text-center" data-testid="payment-failed">
          <p className="font-display text-3xl">{t("payment_fail")}</p>
          <Link to="/" className="link-line mt-8 inline-block pb-1 text-sm">{t("back_home")}</Link>
        </div>
      )}
    </main>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  const { reload } = useStore();
  const [params] = useSearchParams();
  const token = params.get("order");

  useEffect(() => {
    if (!token) return;
    api.post(`/orders/${token}/cancel-payment`).then(() => reload()).catch(() => {});
  }, [token, reload]);
  return (
    <main className="grain relative flex min-h-screen flex-col items-center justify-center bg-[#0B0B0C] px-5 text-center text-[#F7F3EB]" data-testid="payment-cancel-page">
      <p className="eyebrow">ATOR RWIHA</p>
      <h1 className="font-display mt-6 text-4xl sm:text-5xl">{t("payment_cancel_t")}</h1>
      <p className="mt-4 max-w-md text-[#F7F3EB]/75">{t("payment_cancel_d")}</p>
      <Link to="/checkout" data-testid="payment-cancel-retry" className="mt-10 bg-[#C6A76A] px-8 py-4 text-xs tracking-[0.22em] text-[#0B0B0C] rtl:text-sm rtl:tracking-normal">
        {t("checkout")}
      </Link>
      <Link to="/" data-testid="payment-cancel-home" className="btn-gold mt-10 border border-[#C6A76A] px-8 py-4 text-xs tracking-[0.22em] transition-colors duration-500 hover:text-[#0B0B0C] rtl:text-sm rtl:tracking-normal">
        {t("back_home")}
      </Link>
    </main>
  );
}
